import { FormControl, FormGroup } from '@angular/forms';
import {
  ConfigVoiceConfig,
  DeepgramConfig,
  ElevenLabsConfig,
  ModelConfig,
  modelConfigFromJson,
  OpenAIConfig,
  OpenAILiveConfig,
  OpenAIRealtimeConfig,
  VadEagerness,
  VadEagernessValue,
  VadType,
  VendorModel,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { map, of, startWith, switchMap } from 'rxjs';
import { DEFAULT_PROVIDER_VOICES, DEFAULT_VOICE_PROVIDER } from '../ai-assistant.constants';
import { AiVoice, AiVoiceFamily } from '../interfaces/config.interface';
import { familyFromVoice } from '../services/ai-assistant-utils';
import { createElevenLabsVoiceValidator } from './forms.validators';

export type DeepgramConfigFormControls = {
  voice: FormControl<string>;
};

export class DeepgramConfigForm extends FormGroup<DeepgramConfigFormControls> {
  constructor(deepgramConfig?: DeepgramConfig) {
    super({
      voice: new FormControl(
        deepgramConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_DEEPGRAM) ?? '',
        { nonNullable: true },
      ),
    });
  }
}

export type ElevenLabsConfigFormControls = {
  voice: FormControl<string>;
  customVoice: FormControl<string>;
};

export class ElevenLabsConfigForm extends FormGroup<ElevenLabsConfigFormControls> {
  constructor(elevenLabsConfig?: ElevenLabsConfig) {
    super({
      voice: new FormControl(
        elevenLabsConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_ELEVEN_LABS) ?? '',
        { nonNullable: true },
      ),
      // Only the custom voice is validated against ElevenLabs; library voices are pre-vetted and some
      // carry a surcharge that would otherwise wrongly block saving.
      customVoice: new FormControl('', {
        nonNullable: true,
        asyncValidators: [createElevenLabsVoiceValidator()],
      }),
    });
  }
}

export type OpenAIConfigFormControls = {
  voice: FormControl<string>;
};

export class OpenAIConfigForm extends FormGroup<OpenAIConfigFormControls> {
  constructor(openAIConfig?: OpenAIConfig) {
    super({
      voice: new FormControl(openAIConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI)!, {
        nonNullable: true,
      }),
    });
  }
}

export type OpenAILiveConfigFormControls = {
  voice: FormControl<string>;
};

export class OpenAILiveConfigForm extends FormGroup<OpenAILiveConfigFormControls> {
  constructor(openAILiveConfig?: OpenAILiveConfig) {
    super({
      voice: new FormControl(
        openAILiveConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI_LIVE) ?? '',
        { nonNullable: true },
      ),
    });
  }
}

const EAGERNESS_MEDIUM_POSITION = VadEagernessValue[VadEagerness.VAD_EAGERNESS_MEDIUM];

/** The Material slider only accepts numbers, so eagerness is stored as 0, 1 or 2. */
export function eagernessToSliderPosition(eagerness: VadEagerness | undefined): number {
  return VadEagernessValue[eagerness ?? VadEagerness.VAD_EAGERNESS_MEDIUM] ?? EAGERNESS_MEDIUM_POSITION;
}

export function sliderPositionToEagerness(position: number | undefined): VadEagerness {
  const match = (Object.keys(VadEagernessValue) as VadEagerness[]).find((key) => VadEagernessValue[key] === position);
  return match ?? VadEagerness.VAD_EAGERNESS_MEDIUM;
}

export type OpenAIRealtimeConfigFormControls = {
  voice: FormControl<string>;
  turnDetection: FormGroup<{
    vadType: FormControl<VadType>;
    eagerness: FormControl<number>;
    threshold: FormControl<number | null>;
    prefixPadding: FormControl<number | null>;
    silenceDuration: FormControl<number | null>;
  }>;
};

export class OpenAIRealtimeConfigForm extends FormGroup<OpenAIRealtimeConfigFormControls> {
  constructor(openAIRealtimeConfig?: OpenAIRealtimeConfig) {
    const td = openAIRealtimeConfig?.turnDetection;

    super({
      voice: new FormControl(
        openAIRealtimeConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME) ?? '',
        { nonNullable: true },
      ),
      turnDetection: new FormGroup({
        vadType: new FormControl(td?.vadType ?? VadType.VAD_TYPE_SEMANTIC_VAD, {
          nonNullable: true,
        }),
        eagerness: new FormControl(eagernessToSliderPosition(td?.eagerness), { nonNullable: true }),
        threshold: new FormControl(openAIRealtimeConfig?.turnDetection?.threshold ?? 0.75),
        prefixPadding: new FormControl(openAIRealtimeConfig?.turnDetection?.prefixPadding ?? 300),
        silenceDuration: new FormControl(openAIRealtimeConfig?.turnDetection?.silenceDuration ?? 500),
      }),
    });
  }
}

export type ModelConfigFormControls = {
  openAIRealtimeConfig: OpenAIRealtimeConfigForm;
  openAIConfig: OpenAIConfigForm;
  deepgramConfig: DeepgramConfigForm;
  elevenLabsConfig: ElevenLabsConfigForm;
  openAILiveConfig: OpenAILiveConfigForm;
};

export class ModelConfigForm extends FormGroup<ModelConfigFormControls> {
  constructor(modelConfig?: ModelConfig) {
    super({
      openAIRealtimeConfig: new OpenAIRealtimeConfigForm(modelConfig?.openaiRealtimeConfig),
      openAIConfig: new OpenAIConfigForm(modelConfig?.openaiConfig),
      deepgramConfig: new DeepgramConfigForm(modelConfig?.deepgramConfig),
      elevenLabsConfig: new ElevenLabsConfigForm(modelConfig?.elevenLabsConfig),
      openAILiveConfig: new OpenAILiveConfigForm(modelConfig?.openaiLiveConfig),
    });
  }
}

export type AiVoiceConfigFormControls = {
  vendorModel: FormControl<VendorModel | null>;
  voiceFamily: FormControl<AiVoiceFamily | null>;
  modelConfig: ModelConfigForm;
  preGreetingMessage: FormControl<string | null>;
  showPreGreetingMessage: FormControl<boolean>;
};

export class AiVoiceConfigurationForm extends FormGroup<AiVoiceConfigFormControls> {
  static readonly DEPRECATED_VOICES: ReadonlyArray<{ provider: VendorModel; voice: string }> = [
    { provider: VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME, voice: 'sage' },
  ];

  constructor(voiceConfig?: ConfigVoiceConfig) {
    const vendorModel =
      !voiceConfig?.vendorModel || voiceConfig.vendorModel === VendorModel.VENDOR_MODEL_UNSPECIFIED
        ? DEFAULT_VOICE_PROVIDER
        : voiceConfig.vendorModel;
    let voice = DEFAULT_PROVIDER_VOICES.get(DEFAULT_VOICE_PROVIDER)!;
    switch (voiceConfig?.vendorModel) {
      case VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME:
        voice = voiceConfig?.modelConfig?.openaiRealtimeConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_DEEPGRAM:
        voice = voiceConfig?.modelConfig?.deepgramConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_ELEVEN_LABS:
        voice = voiceConfig?.modelConfig?.elevenLabsConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_OPEN_AI:
        voice = voiceConfig?.modelConfig?.openaiConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_OPEN_AI_LIVE:
        voice = voiceConfig?.modelConfig?.openaiLiveConfig?.voice ?? '';
    }
    const voiceFamily = familyFromVoice(voice, vendorModel);
    super({
      vendorModel: new FormControl(vendorModel, {
        nonNullable: true,
      }),
      voiceFamily: new FormControl(voiceFamily ?? null, { nonNullable: true }),
      modelConfig: new ModelConfigForm(voiceConfig?.modelConfig),
      preGreetingMessage: new FormControl(voiceConfig?.preGreetingMessage ?? null, { nonNullable: true }),
      showPreGreetingMessage: new FormControl(!!voiceConfig?.preGreetingMessage, { nonNullable: true }),
    });
  }

  readonly voiceFamily$ = this.controls.voiceFamily.valueChanges.pipe(startWith(this.controls.voiceFamily.value));

  readonly voiceSummary$ = this.voiceFamily$.pipe(
    switchMap((family) => {
      if (!family) return of(null);
      const voiceControl = this.getVoiceControl(family.provider);
      if (!voiceControl) return of({ familyName: family.name, voiceName: undefined });
      return voiceControl.valueChanges.pipe(
        startWith(voiceControl.value),
        map((voiceValue) => ({
          familyName: family.name,
          voiceName: family.voices.find((v) => v.value === voiceValue)?.name,
        })),
      );
    }),
  );

  getVoices(family: AiVoiceFamily | null | undefined): AiVoice[] {
    if (!family?.voices) return [];
    return family.voices.filter(
      (voice) =>
        !AiVoiceConfigurationForm.DEPRECATED_VOICES.some(
          (deprecated) => deprecated.provider === family.provider && deprecated.voice === voice.value,
        ),
    );
  }

  getModelConfigForm(
    vendorModel: VendorModel | null,
  ):
    | OpenAIRealtimeConfigForm
    | DeepgramConfigForm
    | ElevenLabsConfigForm
    | OpenAIConfigForm
    | OpenAILiveConfigForm
    | null {
    const modelConfig = this.controls.modelConfig.controls;
    switch (vendorModel) {
      case VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME:
        return modelConfig.openAIRealtimeConfig;
      case VendorModel.VENDOR_MODEL_OPEN_AI_LIVE:
        return modelConfig.openAILiveConfig;
      case VendorModel.VENDOR_MODEL_DEEPGRAM:
        return modelConfig.deepgramConfig;
      case VendorModel.VENDOR_MODEL_ELEVEN_LABS:
        return modelConfig.elevenLabsConfig;
      case VendorModel.VENDOR_MODEL_OPEN_AI:
        return modelConfig.openAIConfig;
      default:
        return null;
    }
  }

  getVoiceControl(vendorModel: VendorModel | null) {
    return this.getModelConfigForm(vendorModel)?.controls?.voice ?? null;
  }

  getElevenLabsCustomVoiceControl(): FormControl<string> {
    return this.controls.modelConfig.controls.elevenLabsConfig.controls.customVoice;
  }

  toAssistantVoiceConfig(): ConfigVoiceConfig {
    return {
      vendorModel: this.controls?.vendorModel.value ?? VendorModel.VENDOR_MODEL_UNSPECIFIED,
      modelConfig: this.buildVoiceModelConfig(),
      preGreetingMessage: this.controls?.showPreGreetingMessage.value
        ? (this.controls?.preGreetingMessage.value ?? '')
        : '',
    };
  }

  private buildVoiceModelConfig(): ModelConfig {
    const family = this.controls?.voiceFamily?.value;
    const deepgramConfig = this.controls?.modelConfig?.controls?.deepgramConfig?.getRawValue();
    const elevenLabsConfig = this.controls?.modelConfig?.controls?.elevenLabsConfig?.getRawValue();
    const openaiRealtimeConfig = this.controls?.modelConfig?.controls?.openAIRealtimeConfig?.getRawValue();
    const openaiConfig = this.controls?.modelConfig?.controls?.openAIConfig?.getRawValue();
    const openaiLiveConfig = this.controls?.modelConfig?.controls?.openAILiveConfig?.getRawValue();
    // The generated codec fills every proto field, so new provider configs don't break this.
    const empty = modelConfigFromJson({});

    if (family?.provider === VendorModel.VENDOR_MODEL_DEEPGRAM) {
      return { ...empty, deepgramConfig: { voice: deepgramConfig?.voice ?? '' } };
    }

    if (family?.provider === VendorModel.VENDOR_MODEL_ELEVEN_LABS) {
      // A non-empty custom voice means the user opted into a custom ID; the library control is cleared
      // of custom values on switch, so favouring customVoice can't accidentally use a stale library one.
      return { ...empty, elevenLabsConfig: { voice: elevenLabsConfig?.customVoice || elevenLabsConfig?.voice || '' } };
    }

    if (family?.provider === VendorModel.VENDOR_MODEL_OPEN_AI) {
      return { ...empty, openaiConfig: { voice: openaiConfig?.voice ?? '' } };
    }

    // Live has no turn-detection settings of its own, so it carries only a voice.
    if (family?.provider === VendorModel.VENDOR_MODEL_OPEN_AI_LIVE) {
      return { ...empty, openaiLiveConfig: { voice: openaiLiveConfig?.voice ?? '' } };
    }

    return {
      ...empty,
      openaiRealtimeConfig: {
        voice: openaiRealtimeConfig?.voice ?? '',
        turnDetection: {
          vadType: openaiRealtimeConfig?.turnDetection?.vadType ?? VadType.VAD_TYPE_SEMANTIC_VAD,
          eagerness: sliderPositionToEagerness(openaiRealtimeConfig?.turnDetection?.eagerness),
          threshold: openaiRealtimeConfig?.turnDetection?.threshold ?? 0.75,
          prefixPadding: openaiRealtimeConfig?.turnDetection?.prefixPadding ?? 300,
          silenceDuration: openaiRealtimeConfig?.turnDetection?.silenceDuration ?? 500,
        },
      },
    };
  }
}
